import {
  AlertCircle,
  Check,
  ChevronDown,
  CircleDashed,
  LoaderCircle,
  Users,
  Workflow,
} from "lucide-react";
import { useState } from "react";
import type { AgentRun, CouncilContribution } from "../hooks/useChat";
import type { RunRecord, RunStep } from "../lib/types";

const TERMINAL = ["complete", "fallback", "error", "blocked", "aborted", "cost_limit", "skipped"];
const FAILED = ["error", "blocked", "aborted", "cost_limit"];

function statusIcon(status: string) {
  if (status === "running" || status === "planning") {
    return <LoaderCircle className="spin" size={13} />;
  }
  if (status === "complete" || status === "fallback") return <Check size={13} />;
  if (FAILED.includes(status)) return <AlertCircle size={13} />;
  return <CircleDashed size={13} />;
}

function money(value: number): string {
  return `$${value.toFixed(value < 0.01 ? 5 : 3)}`;
}

interface LiveRunProps {
  engine: "council" | "agent_team";
  steps: RunStep[];
  /** When false the step bodies stay hidden while the run is in flight. */
  visible: boolean;
  maxCost: number;
}

/**
 * Progress surface for a run that is still executing.
 *
 * The intermediate reasoning of a Council or Agent Team is deliberately not
 * rendered here by default: watching six models argue is noise, and the only
 * deliverable is the synthesis. The full transcript is frozen onto the message
 * when the run finishes and can be opened from there. Users who do want the
 * live feed opt in with the `visible` setting.
 */
export function LiveRun({ engine, steps, visible, maxCost }: LiveRunProps) {
  if (!steps.length) return null;
  const done = steps.filter((step) => TERMINAL.includes(step.status)).length;
  const running = steps.filter(
    (step) => step.status === "running" || step.status === "planning",
  );
  const knownCost = steps.reduce((sum, step) => sum + (step.usage?.cost ?? 0), 0);
  const isCouncil = engine === "council";

  return (
    <section className={`run-card live ${isCouncil ? "council" : "team"}`}>
      <div className="run-card-head">
        <span className="run-glyph">
          {isCouncil ? <Users size={15} /> : <Workflow size={15} />}
        </span>
        <div className="run-card-title">
          <strong>{isCouncil ? "AI Council" : "Agent Team"}</strong>
          <small>
            {running.length
              ? `${running.map((step) => step.label).join(", ")} · in corso`
              : "sintesi in preparazione"}
          </small>
        </div>
        <span className="run-count">
          {done}/{steps.length}
        </span>
      </div>
      <div className="run-progress">
        <span style={{ width: `${(done / steps.length) * 100}%` }} />
      </div>
      {visible ? (
        <div className="run-steps">
          {steps.map((step) => (
            <div className={`run-step ${step.status}`} key={step.id}>
              <div className="run-step-head">
                {statusIcon(step.status)}
                <strong>{step.label}</strong>
                <span>{step.sublabel}</span>
              </div>
              {step.content && <p>{step.content}</p>}
            </div>
          ))}
        </div>
      ) : (
        <div className="run-pills">
          {steps.map((step) => (
            <span className={`run-pill ${step.status}`} key={step.id} title={step.label}>
              {statusIcon(step.status)}
              {step.label}
            </span>
          ))}
        </div>
      )}
      <div className="run-card-foot">
        <span>
          {money(knownCost)} / {money(maxCost)}
        </span>
      </div>
    </section>
  );
}

/**
 * The frozen transcript attached to a completed synthesis message. Collapsed by
 * default — the answer is the product, the debate is the evidence behind it.
 */
export function RunTranscript({ run }: { run: RunRecord }) {
  const [open, setOpen] = useState(false);
  const isCouncil = run.engine === "council";
  const seconds = Math.max(0, Math.round((run.finishedAt - run.startedAt) / 1000));
  const failures = run.steps.filter((step) => FAILED.includes(step.status)).length;

  return (
    <section className={`run-card record ${isCouncil ? "council" : "team"} ${open ? "open" : ""}`}>
      <button className="run-card-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="run-glyph">
          {isCouncil ? <Users size={15} /> : <Workflow size={15} />}
        </span>
        <div className="run-card-title">
          <strong>{isCouncil ? "AI Council" : "Agent Team"}</strong>
          <small>
            {run.steps.length} {isCouncil ? "contributi" : "fasi"} · {seconds}s ·{" "}
            {run.totalTokens.toLocaleString()} token · {money(run.knownCost)}
            {failures ? ` · ${failures} in errore` : ""}
          </small>
        </div>
        <ChevronDown className="run-chevron" size={15} />
      </button>
      {open && (
        <div className="run-steps">
          {run.steps.map((step) => (
            <div className={`run-step ${step.status}`} key={step.id}>
              <div className="run-step-head">
                {statusIcon(step.status)}
                <strong>{step.label}</strong>
                <span>{step.sublabel}</span>
              </div>
              {step.detail && <p className="run-step-detail">{step.detail}</p>}
              {step.content && <p>{step.content}</p>}
              {step.usage && (
                <div className="run-step-metrics">
                  <span>{step.usage.totalTokens.toLocaleString()} token</span>
                  <span>
                    {step.usage.cost !== undefined
                      ? money(step.usage.cost)
                      : "costo n/d"}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function councilSteps(items: CouncilContribution[]): RunStep[] {
  return items.map((item) => ({
    id: item.id,
    label: item.model || "modello",
    sublabel: `Round ${item.round}`,
    status: item.status,
    content: item.content,
    round: item.round,
    ...(item.usage ? { usage: item.usage } : {}),
  }));
}

export function teamSteps(items: AgentRun[]): RunStep[] {
  return items.map((item) => ({
    id: item.id,
    label: item.name,
    sublabel: item.role,
    status: item.status,
    content: item.content,
    ...(item.detail ? { detail: item.detail } : {}),
    ...(item.usage ? { usage: item.usage } : {}),
  }));
}
